import { useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useViewingOrg } from "@/contexts/viewing-org-context";
import { customInstance } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

interface Organization {
  id: number;
  name: string;
  slug: string;
  status: string;
  is_active: boolean;
  organization_type?: string;
}

interface PaginatedOrganizations {
  results?: Organization[];
  next?: string | null;
}

/** Follow DRF page links until every organization row is loaded. */
async function fetchAllOrganizationsPages(): Promise<Organization[]> {
  const merged: Organization[] = [];
  let nextUrl: string | null = "/api/organizations/";

  while (nextUrl) {
    const { data } = await customInstance.get<Organization[] | PaginatedOrganizations>(nextUrl);
    if (Array.isArray(data)) {
      merged.push(...data);
      break;
    }
    merged.push(...(data.results ?? []));
    nextUrl = data.next ?? null;
  }

  return merged;
}

export function OrgSwitcher() {
  const { user } = useAuth();
  const { viewingOrgId, setViewingOrgId } = useViewingOrg();
  const navigate = useNavigate();
  const location = useLocation();

  const isStaffSwitcher =
    !!user && (user.role === "SUPERADMIN" || user.role === "DD_ACCOUNT_MANAGER");
  /** Demco-style: manufacturer HQ org admins + TSMs pick among child dealers. */
  const isProgramDealerSwitcher =
    !!user &&
    (user.role === "TERRITORY_SALES_MANAGER" ||
      (user.role === "ORG_ADMIN" && user.organization_type === "MANUFACTURER"));
  const canSwitch = isStaffSwitcher || isProgramDealerSwitcher;

  const { data: organizationsResponse, isLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: fetchAllOrganizationsPages,
    enabled: canSwitch,
  });

  const organizations = organizationsResponse as Organization[] | undefined;
  const initialSync = useRef(false);

  useEffect(() => {
    // Staff AMs with a primary org: default the switcher to that org once.
    if (!isStaffSwitcher || initialSync.current || user?.organization == null) return;
    initialSync.current = true;
    setViewingOrgId(user.organization);
  }, [isStaffSwitcher, user?.organization, setViewingOrgId]);

  const options = useMemo(() => {
    const active = (organizations ?? []).filter((org) => org.status === "ACTIVE");
    if (isProgramDealerSwitcher) {
      return active
        .filter((org) => org.organization_type === "DEALER")
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return active.sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations, isProgramDealerSwitcher]);

  const handleOrgChange = (value: string) => {
    setViewingOrgId(value === "" ? null : Number(value));
    const onApplicationDetail = /^\/apps\/\d+$/.test(location.pathname);
    if (onApplicationDetail) {
      navigate("/apps", { replace: true });
    }
  };

  if (!canSwitch) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  // Program switcher: still show even with zero dealers so TSMs know where the list will appear.
  if (!isProgramDealerSwitcher && options.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-xs text-gray-600 shrink-0">
        {isProgramDealerSwitcher ? "Dealer:" : "Viewing:"}
      </span>
      <select
        value={viewingOrgId ?? ""}
        onChange={(e) => handleOrgChange(e.target.value)}
        className="text-xs min-w-[8rem] max-w-[14rem] px-2.5 py-1.5 border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="">
          {isProgramDealerSwitcher ? "All dealers" : "All Organizations"}
        </option>
        {options.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
    </div>
  );
}
